import {
  Grid,
  Paper,
  alpha,
  Typography,
  Box,
  Icon,
  Skeleton,
  Button,
} from "@mui/material";
import React, { Fragment, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { theme } from "../../../../theme";
import { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import { posService } from "../../../../services/pos.service";
import { toast } from "react-toastify";
import { IBucket } from "../../../../app.interface";
import { motion } from "framer-motion";
import moment from "moment";

export const POSDashboardSales: React.FC<{
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  onDateRangeChange?: (
    newRange: [dayjs.Dayjs | null, dayjs.Dayjs | null]
  ) => void;
}> = ({ dateRange, onDateRangeChange }) => {
  const [loading, setLoading] = React.useState(true);
  const [DailySales, setDailySales] = React.useState<IBucket[]>([]);
  const [paymentMethodsData, setPaymentMethodsData] = React.useState<IBucket[]>(
    []
  );

  const isDateRangeTooShort = React.useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return true;
    const days = dateRange[1].diff(dateRange[0], "day");
    return days < 5;
  }, [dateRange]);

  const getSmartDateFormat = React.useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return "ddd";

    const days = dateRange[1].diff(dateRange[0], "day");
    const months = dateRange[1].diff(dateRange[0], "month");

    // If the range spans multiple months or is more than 31 days
    if (months > 0 || days > 31) {
      return "DD MMM"; // e.g., "15 Jan"
    }

    // For ranges within a month but more than a week
    if (days > 7) {
      return "DD ddd"; // e.g., "15 Mon"
    }

    // For ranges within a week
    return "ddd"; // e.g., "Mon"
  }, [dateRange]);

  const dailySalesOptions: ApexOptions = {
    chart: {
      type: "area",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    colors: ["#1E8078"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.3,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
    xaxis: {
      categories: DailySales.map((item) => {
        const date = moment(parseInt(item.key));
        const formattedDate = date.format(getSmartDateFormat);
        return date.isAfter(moment(), "day")
          ? `${formattedDate} (Upcoming)`
          : formattedDate;
      }),
      labels: {
        style: {
          colors: DailySales.map((item) =>
            moment(parseInt(item.key)).isAfter(moment(), "day")
              ? alpha(theme.palette.text.secondary, 0.5)
              : theme.palette.text.secondary
          ),
        },
        rotate: -45,
        rotateAlways: false,
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => Math.round(value).toString(),
        style: {
          colors: theme.palette.text.secondary,
        },
      },
    },
    tooltip: {
      y: {
        formatter: (value) => Math.round(value).toString(),
      },
    },
  };

  const dailySalesData = [
    {
      name: "Orders",
      data: DailySales.map((item) => ({
        x: moment(parseInt(item.key)).format(getSmartDateFormat),
        y: item.doc_count,
        fillColor: moment(parseInt(item.key)).isAfter(moment(), "day")
          ? alpha("#1E8078", 0.1)
          : undefined,
      })),
    },
  ];

  const paymentMethodsOptions: ApexOptions = {
    chart: {
      type: "donut",
    },
    colors: ["#1E8078", "#26A69A", "#4DB6AC", "#80CBC4"],
    labels: paymentMethodsData.map((item) => item.key),
    legend: {
      position: "bottom",
      labels: {
        colors: theme.palette.text.secondary,
      },
    },
    dataLabels: {
      enabled: true,
    },
  };

  const fetchSalesData = () => {
    setLoading(true);
    posService
      .getDashboardSalesData({
        start_date: (dateRange[0]?.unix() ?? 0) * 1000,
        end_date: (dateRange[1]?.unix() ?? 0) * 1000,
      })
      .then((res: any) => {
        setDailySales(res.salesData);
        setPaymentMethodsData(res.paymentMethods);
      })
      .catch(() => {
        toast.error("Error happened while fetching sales data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(fetchSalesData, [dateRange]);

  const handleSelectThisWeek = () => {
    if (onDateRangeChange) {
      onDateRangeChange([dayjs().startOf("week"), dayjs().endOf("week")]);
    }
  };

  const EmptyStateView: React.FC<{
    title: string;
    message: string;
    icon: string;
    height?: number;
    action?: {
      label: string;
      onClick: () => void;
      icon?: string;
    };
  }> = ({ title, message, icon, height = 350, action }) => (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height,
        p: 3,
        textAlign: "center",
      }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: alpha("#1E8078", 0.1),
            color: "#1E8078",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Icon sx={{ fontSize: 40 }}>{icon}</Icon>
        </Box>
      </motion.div>

      <Typography
        variant="h6"
        component={motion.h6}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        gutterBottom
        sx={{ color: "text.primary", fontWeight: 600 }}
      >
        {title}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        component={motion.p}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        sx={{ maxWidth: 400, mb: action ? 4 : 0 }}
      >
        {message}
      </Typography>

      {action && (
        <Button
          variant="contained"
          onClick={action.onClick}
          component={motion.button}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          startIcon={action.icon && <Icon>{action.icon}</Icon>}
          sx={{
            bgcolor: "#1E8078",
            "&:hover": {
              bgcolor: alpha("#1E8078", 0.9),
            },
          }}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );

  const LoadingSkeleton = () => (
    <Box sx={{ width: "100%", height: 350 }}>
      {/* Area Chart Loading Skeleton */}
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{
            borderRadius: 1,
            transform: "none",
          }}
        />
        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              variant="text"
              width={40}
              sx={{ transform: "none" }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );

  const LoadingDonutSkeleton = () => (
    <Box
      sx={{
        width: "100%",
        height: 350,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Skeleton
        variant="circular"
        width={200}
        height={200}
        sx={{ transform: "none" }}
      />
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 3,
        }}
      >
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="text"
            width={50}
            sx={{ transform: "none" }}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Fragment>
      <Grid
        container
        spacing={3}
        sx={{
          m: 0,
          width: "100%",
        }}
      >
        <Grid item xs={12} md={8} sx={{ p: "12px !important" }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(8px)",
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Daily Orders Overview
            </Typography>
            {loading ? (
              <LoadingSkeleton />
            ) : isDateRangeTooShort ? (
              <EmptyStateView
                title="Insufficient Date Range"
                message="Please select a date range of at least 5 days to view the sales trend analysis."
                icon="date_range"
                action={{
                  label: "View This Week's Data",
                  onClick: handleSelectThisWeek,
                  icon: "today",
                }}
              />
            ) : DailySales.length === 0 ? (
              <EmptyStateView
                title="No Orders Data"
                message="There are no orders recorded for the selected date range. Try selecting a different time period."
                icon="shopping_cart"
              />
            ) : (
              <ReactApexChart
                options={dailySalesOptions}
                series={dailySalesData}
                type="area"
                height={350}
              />
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4} sx={{ p: "12px !important" }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(8px)",
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Payment Distribution
            </Typography>
            {loading ? (
              <LoadingDonutSkeleton />
            ) : paymentMethodsData.length === 0 ? (
              <EmptyStateView
                title="No Payment Data"
                message="No payment methods data available for the selected period."
                icon="payments"
                height={320}
              />
            ) : (
              <ReactApexChart
                options={paymentMethodsOptions}
                series={paymentMethodsData.map((item) => item.doc_count)}
                type="donut"
                height={350}
              />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Fragment>
  );
};
